import { useState, useEffect } from "react";
import { useResizeObserver } from "@react-hookz/web";
import { FragmentOf, ResultOf } from "gql.tada";
import { motion, PanInfo, AnimationOptions, Transition } from "motion/react";
import cx from "classnames";
import { RichEntryFragment } from "@queries/fragments/RichEntryFragment";
import { ThemeModel } from "../../../types/ThemeModel";
import { animationCurves } from "@utils/animationCurves";
import { useIsDesktop } from "@hooks/useIsDesktop";
import { Button } from "@components/shared/core/Button";
import CarouselItem from "./CarouselItem";
import { LinkFragment } from "@queries/fragments/LinkFragment";
import { Link } from "@components/shared/core/Link";
import { ArrowButton } from "@components/shared/core/ArrowButton";

export type Slide = {
  content: string;
  image?: string | null;
  video?: string | null;
  icon: string | null;
  sourceInfo?: FragmentOf<typeof RichEntryFragment>;
  highlightTheme?: ThemeModel | null;
};

type TProps = {
  slides: Slide[];
  slideWidth: number;
  slideHeight: number;
  variant?: string;
  gap: number;
  accessibilityControlLabelPrevious: string | null;
  accessibilityControlLabelNext: string | null;
  ctaLink?: ResultOf<typeof LinkFragment> | null;
};

const controlsOffset = 150;
const dragSensitivity = 1.5;

const Carousel = ({
  slides,
  slideWidth,
  slideHeight,
  accessibilityControlLabelPrevious,
  accessibilityControlLabelNext,
  gap,
  ctaLink,
  variant,
}: TProps) => {
  const startSlide = slides.length;
  const totalWidth = slideWidth + gap;
  // Controls current position
  const [offset, setOffset] = useState(startSlide * totalWidth);
  const [direction, setDirection] = useState<1 | -1 | 0>(0);
  const [animationType, setAnimationType] = useState<
    Transition | AnimationOptions
  >(animationCurves.long);
  const extendedSlides = [...slides, ...slides];

  // fewer than 6 slides and the animation breaks
  if (extendedSlides.length < 6) {
    extendedSlides.push(...slides);
  }
  const isDesktop = useIsDesktop();

  useResizeObserver(document.documentElement, () => {
    setOffset(startSlide * totalWidth);
  });

  useEffect(() => {
    setOffset(startSlide * totalWidth);
  }, [startSlide, totalWidth]);

  // buttons controller
  const handleScroll = (direction: 1 | -1 | 0) => {
    setOffset((prevOffset) => prevOffset + direction * totalWidth);
    setDirection(direction);
  };

  // drag controllers
  const handleDrag = (_e: unknown, info: PanInfo) => {
    setOffset((prevOffset) => prevOffset - info.delta.x * dragSensitivity);
    const dir = (-info.delta.x / Math.abs(info.delta.x)) as 1 | -1;
    setDirection(dir);
  };

  const handleDragStart = () => {
    setAnimationType({
      ...animationCurves.easeOut,
    });
  };

  const handleDragEnd = () => {
    const closestSlide = Math.round(offset / totalWidth);
    setOffset(closestSlide * totalWidth);
  };

  return (
    <div className={`relative overflow-x-clip flex flex-col dsk:h-screen ${variant !== 'vertical' && 'justify-center'}`}>
      <div className="relative">
        <motion.div
          className="cursor-grab active:cursor-grabbing flex justify-center items-center"
          style={{
            minHeight: isDesktop ? slideHeight + 54 : slideHeight + 24,
          }}
          drag="x"
          onDrag={handleDrag}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          dragElastic={0}
          dragConstraints={{ left: 0, right: 0 }}
        >
          {/* Slides */}
          {extendedSlides.map((slide, index) => {
            return (
              <CarouselItem
                key={`curve-carousel-item-${index}`}
                index={index}
                slideWidth={slideWidth}
                slideHeight={slideHeight}
                gap={gap}
                offset={offset}
                slide={slide}
                totalSlides={extendedSlides.length}
                direction={direction}
                animationType={animationType as AnimationOptions}
                setAnimationType={setAnimationType}
                variant={variant}
              />
            );
          })}
        </motion.div>
        {!!ctaLink && (
          <div className="hidden dsk:flex w-full justify-center dsk:flex-grow items-center pt-4 dsk:pt-20">
            <Button variant="light-on-blur" size="large">
              <Link link={ctaLink}>{ctaLink.text}</Link>
            </Button>
          </div>
        )}
      </div>

      {/* Carousel buttons */}
      <div className="flex w-full justify-center gap-2">
        <ArrowButton
          onClick={() => handleScroll(-1)}
          className={cx(
            "relative rotate-180 size-12 dsk:absolute dsk:top-[calc(50%-8px)] dsk:size-10"
          )}
          style={{
            left: isDesktop
              ? `calc(50% - ${slideWidth / 2 + controlsOffset}px)`
              : "",
          }}
          ariaLabel={accessibilityControlLabelPrevious}
        />
        <ArrowButton
          onClick={() => handleScroll(1)}
          className="relative size-12 dsk:absolute dsk:top-[calc(50%-8px)] dsk:size-10"
          style={{
            right: isDesktop
              ? `calc(50% - ${slideWidth / 2 + controlsOffset}px)`
              : "",
          }}
          ariaLabel={accessibilityControlLabelNext}
        />
      </div>
      {!!ctaLink && (
        <div className="flex dsk:hidden w-full justify-center dsk:flex-grow items-center pt-4 dsk:pt-20">
          <Button variant="light-on-blur" size="large">
            <Link link={ctaLink}>{ctaLink.text}</Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default Carousel;
